'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { RefObject } from 'react';
import { v4 as uuidv4 } from 'uuid';
import retry from 'async-retry';
import { Buffer } from 'buffer';
import { GraphQLClient } from 'graphql-request';
import { LAST_ALGOLIA_QUERY_ID_KEY } from '@repo/utils/algoliaConstants';
import type { AlgoliaAnalyticsPayload } from '@repo/utils/algoliaAnalyticsAlgV2';
import getCookieDomain from '@repo/utils/getCookieDomain';
import {
  getRakutenAttribution,
  isConsentResolved,
} from '@repo/utils/rakutenAttribution';
import { pushToDataLayer } from '@repo/utils';
import { filterCollectionProductLifecycle } from '@repo/utils/lifecycle';
import { slugifySku } from '@repo/utils/formats';
import { useStatsigClient } from '@repo/statsig-config';
import { trackCartContextEvent } from './cartAnalytics';
import { useUiComponentsInternationalContext } from '@repo/static-translations';
import {
  LOCALIZED_SHOPIFY_COUNTRIES,
  SHOPIFY_COUNTRY_TO_CODE,
  ShopifyCountryCodesEnum,
  ShopifyCountriesEnum,
  useInternationalContext,
} from '@repo/gatsby-localization-helpers';
import { LocaleCurrencyEnum } from '@repo/models/LocaleCurrencyEnum';
import {
  findSellingPlanByDeliveryFrequency,
  type SellingPlanLookupEntry,
} from '@repo/utils/subscription';
import {
  isPixelSupported,
  pixelCategoryFromLocation,
  PIXELS,
  waitForKlaviyo,
} from '@repo/utils/pixels';
import { readBrowserStorage, writeBrowserStorage } from './storageUtil';
import {
  ADD_LINE_ITEM_QUERY,
  CREATE_CART_QUERY,
  GET_CART_QUERY,
  REMOVE_LINE_ITEM_QUERY,
  UPDATE_CART_ATTRIBUTES_QUERY,
  UPDATE_LINE_ITEM_QUERY,
} from './cartQueries';
import { LocaleContext } from './LocaleContext';
import { SiteSettingsContext } from './SiteSettingsContext';

export const PRODUCT_SLUG_ATTRIBUTE_KEY = '_productSlug';
export const PRODUCT_PATHS_ATTRIBUTE_KEY = '_productPaths';

interface ShopifySettings {
  cartIdKey: string;
  domainName?: string;
  accessToken?: string;
}

interface LineItemInput {
  id?: string;
  quantity?: number;
  merchandiseId?: string;
  attributes?: Array<{ key: string; value: string }>;
  sellingPlanId?: string;
}

interface CartLineItem {
  id: string;
  quantity: number;
  merchandise: {
    id: string;
    price?: {
      amount?: number;
      currencyCode?: string;
    };
    product: any;
    sku?: string;
    compareAtPrice?: {
      amount?: number;
      currencyCode?: string;
    };
    image?: {
      src?: string;
    };
  };
  sellingPlanAllocation?: {
    priceAdjustments?: Array<{
      price?: {
        amount?: number;
      };
    }>;
    sellingPlan?: {
      id?: string;
    };
  };
  discountAllocations?: Array<{
    discountedAmount?: {
      amount?: number;
      currencyCode?: string;
    };
    title?: string;
  }>;
  attributes: Array<{ key: string; value: string }>;
}

interface Cart {
  id: string;
  lines: {
    edges: Array<{
      node: CartLineItem;
    }>;
  };
  cost?: {
    subtotalAmount?: {
      currencyCode?: string;
      amount?: number;
    };
    totalAmount?: {
      currencyCode?: string;
      amount?: number;
    };
    totalTaxAmount?: {
      currencyCode?: string;
      amount?: number;
    };
    totalDutyAmount?: {
      currencyCode?: string;
      amount?: number;
    };
  };
  totalQuantity?: number;
  subtotalAmount?: {
    currencyCode?: string;
    amount?: number;
  };
  checkoutUrl?: string;
  attributes?: Array<{ key: string; value: string }>;
}

interface CartProviderProps {
  children: React.ReactNode;
  shopifySettings?: ShopifySettings;
}

interface ToastAddOnsValue {
  imageUrl?: string;
  title?: string;
  price?: number;
  sku?: string;
  cartProps?: {
    productId?: string;
    variantId?: string;
    quantity?: string;
  };
}

interface AddToCartVariant {
  shopifyId?: string;
  sku?: string;
  price?: number;
  compareAtPrice?: number;
  media?: Array<{
    image?: {
      src?: string;
    };
  }>;
}

interface AddToCartProduct {
  shopifyId?: string;
  title?: string;
  vendor?: string;
  shopifyProduct?: {
    shopifyId?: string;
    title?: string;
    vendor?: string;
  };
  store?: {
    previewImageUrl?: string;
    slug?: { current?: string };
  };
  translations?: Array<{
    language?: string;
    store?: {
      slug?: { current?: string };
      status?: string;
      productLifecycle?: string;
      variants?: Array<{ sku?: string }>;
    };
  }>;
}

interface AddToCartArgs {
  activeVariant?: AddToCartVariant;
  addOns?: ToastAddOnsValue[];
  product?: AddToCartProduct;
  quantity: number;
  selectedSellingPlan?: {
    id?: string;
    name?: string;
    percentageDiscount?: number;
  };
  sellingPlanGroups?: SellingPlanLookupEntry[];
  variantId: string;
  variantMetafields?: Array<{
    name?: string | null;
    key?: string | null;
    value?: string | null;
  }>;
  cameFromPLP?: boolean;
  cameFromSearch?: boolean;
  cameFromRecommend?: boolean;
  source?: string;
  modelType?: string;
}

function buildProductPathMap(
  product: AddToCartArgs['product'],
  addedSku?: string
): string | null {
  const translations = product?.translations;
  if (!translations?.length) return null;

  const pathMap: Record<string, string> = {};

  for (const translation of translations) {
    if (!translation) continue;
    if (translation.store?.status !== 'active') continue;
    const lifecycle = translation.store?.productLifecycle;
    if (lifecycle && !filterCollectionProductLifecycle(translation)) continue;
    const lang = translation.language;
    const slug = translation.store?.slug?.current;
    if (!lang || !slug) continue;

    const matchedSku =
      translation.store?.variants?.find(v => v.sku === addedSku)?.sku ??
      translation.store?.variants?.[0]?.sku;

    if (!matchedSku) continue;

    pathMap[lang] = `${slug}/${slugifySku(matchedSku)}`;
  }

  if (!Object.keys(pathMap).length) return null;

  return JSON.stringify(pathMap);
}

interface CartContextValue {
  addToCart: (args: AddToCartArgs) => Promise<boolean>;
  updateLineItems: (args: LineItemInput[]) => Promise<boolean>;
  incrementLineItem: (lineItemId: string) => Promise<void>;
  decrementLineItem: (lineItemId: string) => Promise<void>;
  emptyCart: () => Promise<void>;
  deleteLineItem: (lineItemId: string) => Promise<void>;
  setCartActive: (state: boolean) => void;
  hideToastNotification: () => void;
  hideToast: () => void;
  cart: Cart;
  cartActive: boolean;
  cartBusy: boolean;
  cartError: boolean;
  cartTriggerRef: RefObject<HTMLElement | null>;
  showToast: boolean;
  toastProduct?: CartLineItem;
  toastAddOns?: ToastAddOnsValue[];
  updateCartAttributes: (
    attributes: Array<{ key: string; value: string }>
  ) => Promise<void>;
  shopifyClient: GraphQLClient;
  formatPrice: (price: number, fractionDigits?: number) => string;
}

const defaultState: CartContextValue = {
  addToCart: async () => false,
  updateLineItems: async () => false,
  incrementLineItem: async () => {},
  decrementLineItem: async () => {},
  emptyCart: async () => {},
  deleteLineItem: async () => {},
  setCartActive: () => {},
  hideToastNotification: () => {},
  hideToast: () => {},
  cart: {
    id: '',
    lines: {
      edges: [],
    },
  },
  cartActive: false,
  cartBusy: false,
  cartError: false,
  cartTriggerRef: { current: null },
  showToast: false,
  toastProduct: null,
  toastAddOns: [],
  updateCartAttributes: async () => {},
  shopifyClient: new GraphQLClient(''),
  formatPrice: () => '',
};

const formatCadPrice = (price: number, fractionDigits?: number) => {
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'CAD',
  };

  if (fractionDigits !== undefined) {
    options.minimumFractionDigits = fractionDigits;
    options.maximumFractionDigits = fractionDigits;
  }

  // CAD's currency symbol is rendered inconsistently across ICU versions
  // (the build environment resolves "$" while Chrome resolves "CA$"), which
  // produces a React hydration mismatch (#418) on en-CA pages. CAD's native
  // locale (en-CA) yields "$" deterministically everywhere, so format with it
  // and disambiguate to "CA$" the same way AUD is handled in formatLocalizedPrice.
  return new Intl.NumberFormat('en-CA', options)
    .format(price)
    .replace('$', 'CA$');
};

const CAD_CART_KEY_IDENTIFIER = '_cadCartId';
const SUBSCRIPTION_FREQUENCY_METAFIELD = 'subscription_frequency';
const CART_API_VERSION = '2025-04';
const LINE_ITEM_RECENCY_KEY = 'lineItemRecency';

function isCart(value: unknown): value is Cart {
  return !!value && typeof value === 'object' && Object.hasOwn(value, 'id');
}

const CartContext = createContext(defaultState);

const CartProvider: React.FC<CartProviderProps> = ({
  children,
  shopifySettings = {
    cartIdKey: '_cartId',
    domainName: process.env.GATSBY_SHOPIFY_DOMAIN_NAME,
    accessToken: process.env.GATSBY_SHOPIFY_ACCESS_TOKEN,
  },
}) => {
  const localeContext = useContext(LocaleContext);
  const { siteIdentifier, algoliaSearchSettings } =
    useContext(SiteSettingsContext);
  const { localizePrice } = useUiComponentsInternationalContext();
  const { locale } = useInternationalContext({});
  const currency = localeContext?.currency ?? LocaleCurrencyEnum.USD;
  const { client: statsigClient } = useStatsigClient();

  const shopifyLocaleVars = useMemo(() => {
    const language = locale?.split('-')[0]?.toUpperCase() || 'EN';
    let country =
      SHOPIFY_COUNTRY_TO_CODE[
        LOCALIZED_SHOPIFY_COUNTRIES[locale] as ShopifyCountriesEnum
      ] ?? ShopifyCountryCodesEnum.UNITED_STATES;
    if (currency === LocaleCurrencyEnum.CAD) {
      country = ShopifyCountryCodesEnum.CANADA;
    }
    return { language, country };
  }, [locale, currency]);

  const [cart, setCart] = useState<Cart>(defaultState.cart);
  const [cartActive, setCartActive] = useState<boolean>(
    defaultState.cartActive
  );
  const [cartBusy, setCartBusy] = useState<boolean>(defaultState.cartBusy);
  const [cartError, setCartError] = useState<boolean>(defaultState.cartError);
  const cartTriggerRef = useRef<HTMLElement | null>(null);
  const cartRef = useRef<Cart>(defaultState.cart);
  const cartUpdateQueueRef = useRef<Promise<Cart | null>>(
    Promise.resolve(null)
  );
  // Line item ids in most-recently-added-first order. Shopify merges repeat
  // adds without moving the existing line, so recency is tracked here and the
  // cart lines are re-sorted before every state update. Persisted to
  // sessionStorage so a page refresh keeps the order until the next add.
  const lineItemRecencyRef = useRef<string[]>([]);
  const setLineItemRecency = (next: string[]) => {
    lineItemRecencyRef.current = next;
    sessionStorage.setItem(LINE_ITEM_RECENCY_KEY, JSON.stringify(next));
  };
  const [showToast, setShowToast] = useState<boolean>(defaultState.showToast);
  const [toastProduct, setToastProduct] = useState<CartLineItem>(
    defaultState.toastProduct
  );
  const [toastAddOns, setToastAddOns] = useState<ToastAddOnsValue[]>(
    defaultState.toastAddOns
  );

  const shopifySettingsForCurrency = useMemo(
    () =>
      currency === LocaleCurrencyEnum.CAD
        ? {
            domainName: process.env.GATSBY_SHOPIFY_CAD_DOMAIN_NAME,
            accessToken: process.env.GATSBY_SHOPIFY_CAD_ACCESS_TOKEN,
            cartIdKey: CAD_CART_KEY_IDENTIFIER,
          }
        : {
            domainName: shopifySettings.domainName,
            accessToken: shopifySettings.accessToken,
            cartIdKey: shopifySettings.cartIdKey,
          },
    [
      currency,
      shopifySettings.accessToken,
      shopifySettings.cartIdKey,
      shopifySettings.domainName,
    ]
  );

  const shopifyClient = useMemo(
    () =>
      new GraphQLClient(
        `https://${shopifySettingsForCurrency.domainName}/api/${CART_API_VERSION}/graphql.json`,
        {
          headers: {
            'X-Shopify-Storefront-Access-Token':
              shopifySettingsForCurrency.accessToken,
          },
        }
      ),
    [
      shopifySettingsForCurrency.accessToken,
      shopifySettingsForCurrency.domainName,
    ]
  );

  const setCartActiveHandler = (activeState: boolean) => {
    setCartActive(activeState);
    setShowToast(false);
  };

  const createNewCart = async (client: GraphQLClient) => {
    try {
      let countryCode: ShopifyCountryCodesEnum;

      if (siteIdentifier === 'ifb') {
        countryCode =
          (localeContext?.getCountryCode() as ShopifyCountryCodesEnum) ??
          ShopifyCountryCodesEnum.UNITED_STATES;
      } else {
        countryCode = shopifyLocaleVars.country;
      }

      const input = {
        attributes: process.env.GATSBY_RSC_ENV
          ? [
              {
                key: 'rscEnv',
                value: process.env.GATSBY_RSC_ENV,
              },
            ]
          : [],
        buyerIdentity: {
          countryCode,
        },
      };

      const newCart = await client.request(CREATE_CART_QUERY, {
        input,
        ...shopifyLocaleVars,
      });
      writeBrowserStorage(
        shopifySettingsForCurrency.cartIdKey,
        newCart?.cartCreate?.cart?.id ?? '',
        getCookieDomain()
      );

      // Clear algolia cart session data when creating new cart
      sessionStorage.removeItem('algoliaCartData');
      sessionStorage.removeItem(LINE_ITEM_RECENCY_KEY);
      lineItemRecencyRef.current = [];

      return newCart?.cartCreate?.cart;
    } catch (error) {
      console.error('createNewCart error:', error);
    }

    console.error('Something went wrong when trying to create new cart.');
    return null;
  };

  const initializeCart = async (
    client: GraphQLClient,
    shopifySettingsArg: ShopifySettings
  ) => {
    const cartId = readBrowserStorage(shopifySettingsArg.cartIdKey);
    if (cartId) {
      const existingCart = await client
        .request(GET_CART_QUERY, {
          id: cartId,
          ...shopifyLocaleVars,
        })
        .catch(() => {
          console.warn(
            'Could not retrieve cart based on stored ID, creating a new cart.'
          );
          return createNewCart(client);
        });

      if (!existingCart?.cart) {
        console.warn(
          'Fetch cart call returned null value (stale cart). Creating new cart.'
        );
        return createNewCart(client);
      }

      const cart = existingCart.cart;
      if (!isCart(cart)) {
        console.warn(
          'Fetched cart does not have required id property. Creating new cart.'
        );
        return createNewCart(client);
      }

      return cart;
    }

    console.info(
      'Could not retrieve cart based on stored ID, created a new cart'
    );
    return createNewCart(client);
  };

  const applyCartOrder = (newCart: Cart) => {
    const recency = lineItemRecencyRef.current;
    if (!recency.length || !newCart?.lines?.edges?.length) {
      return newCart;
    }

    const edges = [...newCart.lines.edges].sort((a, b) => {
      const aRank = recency.indexOf(a.node.id);
      const bRank = recency.indexOf(b.node.id);
      // Items absent from the recency list keep their relative (Shopify) order.
      if (aRank === -1 && bRank === -1) return 0;
      if (aRank === -1) return 1;
      if (bRank === -1) return -1;
      return aRank - bRank;
    });

    return { ...newCart, lines: { edges } };
  };

  const updateCart = (newCart: Cart) => {
    if (isCart(newCart)) {
      setCart(applyCartOrder(newCart));
    }
  };

  const updateLineItems = async (
    lineItemsToUpdate: LineItemInput[],
    overrideCartId?: string
  ): Promise<boolean> => {
    setCartBusy(true);

    try {
      const newCartUpdateLine = await shopifyClient.request(
        UPDATE_LINE_ITEM_QUERY,
        {
          cartId: overrideCartId || cart.id,
          lines: lineItemsToUpdate,
          ...shopifyLocaleVars,
        }
      );

      updateCart(newCartUpdateLine.cartLinesUpdate.cart);
      setCartBusy(false);
      return true;
    } catch (error) {
      console.error('updateLineItem error:', lineItemsToUpdate, error);
      setCartBusy(false);
      setCartError(true);
      return false;
    }
  };

  const updateCartAttributes = (
    attributes: Array<{ key: string; value: string }>
  ): Promise<void> => {
    if (!attributes) return Promise.resolve();

    cartUpdateQueueRef.current = cartUpdateQueueRef.current.then(
      async previousCart => {
        try {
          const cartId = readBrowserStorage(
            shopifySettingsForCurrency.cartIdKey
          );

          // Use the previous write's response as the merge base so concurrent
          // callers never overwrite each other's keys. Fall back to cartRef when
          // this is the first write in the queue.
          const currentAttributes =
            (previousCart ?? cartRef.current).attributes ?? [];

          // Merge: callers only supply the keys they own. An empty string value
          // signals removal — that key is stripped from both sets before sending.
          const incomingKeys = new Set(attributes.map(a => a.key));
          const merged = [
            ...currentAttributes.filter(
              a => !incomingKeys.has(a.key) && a.value !== ''
            ),
            ...attributes.filter(a => a.value !== ''),
          ];

          const updatedCart = await shopifyClient.request(
            UPDATE_CART_ATTRIBUTES_QUERY,
            {
              cartId,
              attributes: merged,
              ...shopifyLocaleVars,
            }
          );

          const cart = updatedCart?.cartAttributesUpdate?.cart;
          if (isCart(cart)) {
            setCart(applyCartOrder(cart));
            return cart;
          }
          return previousCart;
        } catch (err) {
          console.error('updateCartAttributes error:', err);
          setCartError(true);
          return previousCart;
        } finally {
          setCartBusy(false);
        }
      }
    );

    return cartUpdateQueueRef.current.then(() => {});
  };

  const getAlgoliaUserTokens = () => {
    const userToken = globalThis?.localStorage?.getItem('uuid');
    if (!userToken) return {};

    const isAuthenticated = userToken?.startsWith('shopify-customer-');
    if (!isAuthenticated) {
      return { userToken };
    }

    const ANON_SESSION_TOKEN_KEY = 'algoliaAnonSessionToken';
    let anonSessionToken = sessionStorage.getItem(ANON_SESSION_TOKEN_KEY);
    if (!anonSessionToken) {
      anonSessionToken = uuidv4();
      sessionStorage.setItem(ANON_SESSION_TOKEN_KEY, anonSessionToken);
    }

    return {
      userToken: anonSessionToken,
      authenticatedUserToken: userToken,
    };
  };

  const createAndStoreAlgoliaData = async (
    product: AddToCartProduct | undefined,
    activeVariant: AddToCartVariant | undefined,
    quantity: number,
    addedLineItem: any,
    attribution: {
      cameFromSearch: boolean;
      queryID?: string | null;
      cameFromPLP?: boolean;
      cameFromRecommend?: boolean;
      source?: string;
      modelType?: string;
    }
  ) => {
    const {
      cameFromSearch,
      queryID,
      cameFromPLP,
      cameFromRecommend,
      source,
      modelType,
    } = attribution;
    const { productIndexName } = algoliaSearchSettings;

    const productId =
      product?.shopifyProduct?.shopifyId?.replace(
        'gid://shopify/Product/',
        ''
      ) ?? product?.shopifyId?.replace('gid://shopify/Product/', '');

    if (!productId || !addedLineItem) return;

    const algoliaData: AlgoliaAnalyticsPayload = {
      productId,
      quantity,
      originalPrice: activeVariant?.price ?? 0,
      currency,
      ...getAlgoliaUserTokens(),
    };

    algoliaData.cameFromSearch = cameFromSearch;
    algoliaData.cameFromPLP = cameFromPLP;
    algoliaData.cameFromRecommend = cameFromRecommend;
    algoliaData.source = source;
    algoliaData.modelType = modelType;

    if ((cameFromSearch || cameFromPLP || cameFromRecommend) && queryID) {
      algoliaData.queryID = queryID;
    }

    const existingData = sessionStorage.getItem('algoliaCartData');
    const algoliaArray = existingData ? JSON.parse(existingData) : [];

    const existingIndex = algoliaArray.findIndex(
      item =>
        item.productId === productId &&
        item.cameFromSearch === cameFromSearch &&
        item.cameFromPLP === cameFromPLP &&
        item.cameFromRecommend === cameFromRecommend &&
        item.source === source &&
        item.modelType === modelType
    );

    if (existingIndex >= 0) {
      algoliaArray[existingIndex].quantity += quantity;
      if (cameFromRecommend && queryID) {
        algoliaArray[existingIndex].queryID = queryID;
      }
    } else {
      algoliaArray.push(algoliaData);
    }

    sessionStorage.setItem('algoliaCartData', JSON.stringify(algoliaArray));
    sessionStorage.setItem('algoliaIndexName', productIndexName);

    const storedAlgoliaData = sessionStorage.getItem('algoliaCartData');
    if (storedAlgoliaData) {
      updateCartAttributes([
        {
          key: 'algoliaCartData',
          value: storedAlgoliaData,
        },
        {
          key: 'algoliaIndexName',
          value: productIndexName,
        },
      ]);
    }
  };

  const trackKlaviyoAddToCart = (
    product: AddToCartProduct | undefined,
    activeVariant: AddToCartVariant | undefined,
    quantity: number,
    newCartAddLine: any,
    locale: string
  ) => {
    if (!isPixelSupported(PIXELS.klaviyo)) return;

    const SHOPIFY_PRODUCT_PREFIX = 'gid://shopify/Product/';
    const SHOPIFY_VARIANT_PREFIX = 'gid://shopify/ProductVariant/';

    const klaviyoItem = {
      ProductName: product?.shopifyProduct?.title || product?.title,
      ProductID:
        product?.shopifyProduct?.shopifyId?.replace(
          SHOPIFY_PRODUCT_PREFIX,
          ''
        ) ?? product?.shopifyId?.replace(SHOPIFY_PRODUCT_PREFIX, ''),
      VariantID: activeVariant?.shopifyId?.replace(SHOPIFY_VARIANT_PREFIX, ''),
      shopifyId: product?.shopifyProduct?.shopifyId || product?.shopifyId,
      SKU: activeVariant?.sku,
      ImageURL:
        activeVariant?.media?.[0]?.image?.src ||
        product?.store?.previewImageUrl,
      Brand: product?.shopifyProduct?.vendor || product?.vendor,
      Price: activeVariant?.price,
      CompareAtPrice: activeVariant?.compareAtPrice,
      URL: globalThis.location.href,
      locale: locale || 'en-us',
    };

    const latestCart = newCartAddLine.cartLinesAdd.cart;

    const newItem = {
      $value: latestCart.cost.subtotalAmount.amount,
      AddedItemProductName: klaviyoItem.ProductName,
      AddedItemProductID: klaviyoItem.ProductID,
      AddedItemSKU: klaviyoItem.SKU,
      AddedItemCategories: [],
      AddedItemImageURL: klaviyoItem.ImageURL,
      AddedItemURL: globalThis.location.href,
      AddedItemPrice: klaviyoItem.Price,
      AddedItemQuantity: quantity,
      locale: locale || 'en-us',
      ItemNames: latestCart.lines.edges.map(
        ({ node }) => node.merchandise.product.title
      ),
      CheckoutURL: latestCart.checkoutUrl,
      Items: latestCart.lines.edges.map(({ node }) => ({
        ProductID: node.merchandise.product.id.replace(
          SHOPIFY_PRODUCT_PREFIX,
          ''
        ),
        VariantID: node.merchandise.id?.replace(SHOPIFY_VARIANT_PREFIX, ''),
        SKU: node.merchandise.sku,
        ProductName: node.merchandise.product.title,
        Quantity: node.quantity,
        ItemPrice: node.merchandise.price.amount,
        CompareAtPrice: node.merchandise?.compareAtPrice?.amount,
        Brand: node.merchandise.product.vendor,
        shopifyId: node.merchandise.product.id,
        RowTotal: node.merchandise.price.amount * node.quantity,
        ImageURL: node.merchandise.image.url,
        ProductCategories: [],
      })),
    };

    waitForKlaviyo(() => {
      globalThis?.klaviyo?.track('Added to cart', newItem);
    }, 2000);
  };

  const trackAdrollAddToCart = (
    activeVariant: AddToCartVariant | undefined,
    quantity: number,
    addOns: ToastAddOnsValue[] | undefined,
    currency: string
  ) => {
    if (!isPixelSupported(PIXELS.adroll)) return;

    globalThis?.adroll?.track('pageView', { segment_name: 'e7d960f1' });

    const mappedAddonAdrollProducts =
      addOns?.map(s => ({
        product_id: s.sku,
        quantity: s.cartProps.quantity,
        category: pixelCategoryFromLocation(globalThis?.location),
      })) || [];

    globalThis?.adroll?.track('addToCart', {
      currency,
      products: [
        {
          product_id: activeVariant?.sku,
          quantity,
          price: activeVariant?.price,
          category: pixelCategoryFromLocation(globalThis?.location),
        },
        ...mappedAddonAdrollProducts,
      ],
    });
  };

  /**
   * Adds product to cart. Repeated adds increment quantity.
   */
  const addToCart = async ({
    activeVariant,
    addOns,
    product,
    quantity,
    selectedSellingPlan,
    sellingPlanGroups,
    variantId,
    variantMetafields,
    cameFromPLP,
    cameFromSearch,
    cameFromRecommend,
    source,
    modelType,
  }: AddToCartArgs): Promise<boolean> => {
    const activeElement = globalThis.document?.activeElement;

    if (
      activeElement instanceof HTMLElement &&
      activeElement !== globalThis.document.body
    ) {
      cartTriggerRef.current = activeElement;
    }

    setCartBusy(true);

    const isCurrentPageSearch =
      globalThis.location.pathname.includes('/search');

    const cameFromSearchPage =
      globalThis.document?.referrer?.includes('/search');

    const hasSearchQueryParam =
      globalThis.location.search.includes('nav=search');

    const hasPLPSearchQueryParam =
      globalThis.location.search.includes('fromPLP=true');

    const globalCameFromSearch =
      isCurrentPageSearch || cameFromSearchPage || hasSearchQueryParam;

    const queryID = sessionStorage.getItem(LAST_ALGOLIA_QUERY_ID_KEY);

    try {
      const lineItem: LineItemInput = {
        quantity,
        merchandiseId: Buffer.from(variantId).toString('base64'),
        attributes: [],
      };

      const productPaths = buildProductPathMap(product, activeVariant?.sku);
      const productSlug = product?.store?.slug?.current;
      if (productPaths) {
        lineItem.attributes.push({
          key: PRODUCT_PATHS_ATTRIBUTE_KEY,
          value: productPaths,
        });
      } else if (productSlug) {
        // Store _productPaths when translations are available, otherwise fall back to _productSlug.
        // Differing slugs per locale cause Shopify to create duplicate line items instead of merging quantities.
        lineItem.attributes.push({
          key: PRODUCT_SLUG_ATTRIBUTE_KEY,
          value: productSlug,
        });
      }

      // IFB subscriptions
      const productDeliveryFrequency = variantMetafields?.find(
        metafield =>
          metafield.name === SUBSCRIPTION_FREQUENCY_METAFIELD ||
          metafield.key === SUBSCRIPTION_FREQUENCY_METAFIELD
      )?.value;

      const sellingPlanId = findSellingPlanByDeliveryFrequency(
        productDeliveryFrequency,
        sellingPlanGroups
      )?.id;
      const resolvedSellingPlanId = selectedSellingPlan?.id || sellingPlanId;

      if (resolvedSellingPlanId) {
        lineItem.sellingPlanId = Buffer.from(resolvedSellingPlanId).toString(
          'base64'
        );
      }

      // PetSafe subscriptions
      if (selectedSellingPlan?.id) {
        if (selectedSellingPlan.name) {
          lineItem.attributes.push({
            key: 'Auto-renews',
            value: selectedSellingPlan.name,
          });
        }

        if (selectedSellingPlan.percentageDiscount) {
          lineItem.attributes.push({
            key: 'Subscription Discount Applied',
            value: `${selectedSellingPlan.percentageDiscount}%`,
          });
        }
      }

      let cartId = readBrowserStorage(shopifySettingsForCurrency.cartIdKey);
      if (!cartId) {
        const newCartCreated = await createNewCart(shopifyClient);
        cartId = newCartCreated?.id;
      }

      setToastAddOns([]);
      let addOnLineItems = [];
      if (addOns?.length > 0) {
        addOnLineItems = addOns.map(({ cartProps }) => ({
          quantity: 1,
          merchandiseId: Buffer.from(cartProps.variantId).toString('base64'),
          attributes: [],
        }));
        setToastAddOns(addOns);
      }

      if (!cartId) {
        throw new Error('cartId could not be established.');
      }

      const newCartAddLine = await shopifyClient.request(ADD_LINE_ITEM_QUERY, {
        cartId,
        lines: [lineItem, ...addOnLineItems],
        ...shopifyLocaleVars,
      });

      const updatedCart = newCartAddLine?.cartLinesAdd?.cart;

      if (!updatedCart) {
        throw new Error('Add to cart failed.');
      }

      let addedLineItem = updatedCart.lines.edges.find(
        ({ node }) =>
          node.merchandise.id === variantId &&
          node?.sellingPlanAllocation?.sellingPlan?.id === resolvedSellingPlanId
      );

      // Try to find line item without selectedSellingPlan
      addedLineItem ??= updatedCart.lines.edges.find(
        ({ node }) => node.merchandise.id === variantId
      );

      // Promote the just-added line item to the top of the recency list. Done
      // before the cart is set below so both the discount and non-discount
      // branches render with the item already promoted.
      const addedLineItemId = addedLineItem?.node?.id;
      if (addedLineItemId) {
        setLineItemRecency([
          addedLineItemId,
          ...lineItemRecencyRef.current.filter(id => id !== addedLineItemId),
        ]);
      }

      const discounts = addedLineItem?.node?.discountAllocations ?? [];

      // Add a line item for applied promotions, if they exist...KA
      // Skip the intermediate updateCart to avoid showing a duplicate line
      // item before Shopify merges them — updateLineItems handles the final state
      if (discounts.length && addedLineItem) {
        await updateLineItems(
          [
            {
              id: addedLineItem.node.id,
              attributes: [
                ...addedLineItem.node.attributes,
                {
                  key: 'Promotions Applied',
                  value: discounts
                    .map(d => d.title)
                    .filter(Boolean)
                    .join(', '),
                },
              ],
            },
          ],
          updatedCart.id
        );
      } else {
        updateCart(updatedCart);
      }

      // Store algolia data in session storage after successful cart update
      await createAndStoreAlgoliaData(
        product,
        activeVariant,
        quantity,
        addedLineItem,
        {
          cameFromSearch: cameFromSearch ?? globalCameFromSearch,
          queryID,
          cameFromPLP: cameFromPLP ?? hasPLPSearchQueryParam,
          cameFromRecommend,
          source,
          modelType,
        }
      );

      // Also track Algolia data for add-ons (Commonly purchased with items)
      if (addOns?.length > 0) {
        for (const addOn of addOns) {
          const addOnLineItem =
            newCartAddLine.cartLinesAdd.cart?.lines.edges.find(
              ({ node }) => node.merchandise.id === addOn.cartProps?.variantId
            );

          if (addOnLineItem && addOn.cartProps?.productId) {
            const addOnProduct = {
              shopifyId: addOn.cartProps.productId,
            };

            const addOnVariant = {
              price: addOn.price,
              sku: addOn.sku,
            };

            await createAndStoreAlgoliaData(
              addOnProduct,
              addOnVariant,
              1,
              addOnLineItem,
              {
                cameFromSearch: false,
                queryID: null,
              }
            );
          }
        }
      }

      setCartBusy(false);
      setShowToast(true);
      setToastProduct(addedLineItem.node);

      trackKlaviyoAddToCart(
        product,
        activeVariant,
        quantity,
        newCartAddLine,
        locale
      );
      trackAdrollAddToCart(activeVariant, quantity, addOns, currency);
      trackCartContextEvent('add_to_cart', statsigClient, activeVariant?.sku, {
        productName: product?.shopifyProduct?.title || product?.title || '',
        quantity: String(quantity),
      });
      return true;
    } catch (error) {
      console.error('addToCart had error:', error);
      setCartBusy(false);
      setCartError(true);
      return false;
    }
  };

  const findLineItem = (lineItemId: string) =>
    cart.lines.edges.find(({ node }) => node.id === lineItemId);

  const updateAlgoliaSessionStorage = async (
    lineItem: CartLineItem,
    quantityChange: number
  ) => {
    const existingData = sessionStorage.getItem('algoliaCartData');
    const algoliaArray = existingData ? JSON.parse(existingData) : [];

    const productId =
      lineItem?.merchandise?.product?.id?.replace(
        'gid://shopify/Product/',
        ''
      ) ?? null;

    let toRemove = 0;
    if (quantityChange < 0) toRemove = -quantityChange;

    let incrementApplied = false;
    let foundMatch = false;
    const survivingRecords = [];

    for (const record of algoliaArray) {
      if (record.productId === productId) {
        foundMatch = true;

        if (toRemove > 0) {
          const consumed = Math.min(record.quantity, toRemove);
          record.quantity -= consumed;
          toRemove -= consumed;
        } else if (quantityChange > 0 && !incrementApplied) {
          record.quantity += quantityChange;
          incrementApplied = true;
        }
      }

      if (record.quantity > 0) survivingRecords.push(record);
    }

    if (!foundMatch) return;

    sessionStorage.setItem('algoliaCartData', JSON.stringify(survivingRecords));

    updateCartAttributes([
      {
        key: 'algoliaCartData',
        value: JSON.stringify(survivingRecords),
      },
    ]);
  };

  const incrementLineItem = async (lineItemId: string) => {
    const lineItem = findLineItem(lineItemId);

    if (lineItem) {
      const isUpdated = await updateLineItems([
        { id: lineItem.node.id, quantity: lineItem.node.quantity + 1 },
      ]);

      if (!isUpdated) return;

      await updateAlgoliaSessionStorage(lineItem.node, 1);

      const lineItemPrice = lineItem?.node?.merchandise?.price?.amount ?? 0;

      if (isPixelSupported(PIXELS.gtm)) {
        pushToDataLayer('add_to_cart', {
          ecommerce: {
            currency,
            value: Number(lineItemPrice).toFixed(2),
          },
          items: [
            {
              variant: lineItem?.node?.merchandise,
              product: lineItem?.node?.merchandise?.product,
              quantity: 1,
            },
          ],
        });
      }
    }
  };

  /**
   * Decrease qty of lineItem. Will remove lineItem if qty is 0
   */
  const decrementLineItem = async (lineItemId: string) => {
    const lineItem = findLineItem(lineItemId);

    if (lineItem) {
      const isUpdated = await updateLineItems([
        { id: lineItem.node.id, quantity: lineItem.node.quantity - 1 },
      ]);

      if (!isUpdated) return;

      const removedLinePrice = lineItem?.node?.merchandise?.price?.amount ?? 0;

      await updateAlgoliaSessionStorage(lineItem.node, -1);

      if (isPixelSupported(PIXELS.gtm)) {
        pushToDataLayer('remove_from_cart', {
          ecommerce: {
            currency,
            value: Number(removedLinePrice).toFixed(2),
          },
          items: [
            {
              variant: lineItem?.node?.merchandise,
              product: lineItem?.node?.merchandise?.product,
              quantity: 1,
            },
          ],
        });
      }
    }
  };

  const emptyCart = async () => {
    setCartBusy(true);

    try {
      const allLineItemIds = cart.lines.edges.map(({ node }) => node.id);

      const newCartRemoveLine = await shopifyClient.request(
        REMOVE_LINE_ITEM_QUERY,
        {
          cartId: cart.id,
          lineIds: allLineItemIds,
          ...shopifyLocaleVars,
        }
      );

      // Clear Algolia session storage when emptying cart
      sessionStorage.removeItem('algoliaCartData');

      setLineItemRecency([]);
      updateCart(newCartRemoveLine.cartLinesRemove.cart);
      setCartBusy(false);
    } catch (error) {
      console.error('emptyCart error:', error);
      setCartBusy(false);
    }
  };

  const deleteLineItem = async (lineItemId: string) => {
    setCartBusy(true);

    try {
      const lineItem = findLineItem(lineItemId);
      const removedLinePrice = lineItem?.node?.merchandise?.price?.amount ?? 0;
      const removedLineQuantity = lineItem?.node?.quantity ?? 0;
      const totalPrice = removedLinePrice * removedLineQuantity;

      // Update Algolia session storage before deleting
      if (lineItem) {
        await updateAlgoliaSessionStorage(lineItem.node, -removedLineQuantity);
      }

      const newCartRemoveLine = await shopifyClient.request(
        REMOVE_LINE_ITEM_QUERY,
        {
          cartId: cart.id,
          lineIds: [lineItemId],
          ...shopifyLocaleVars,
        }
      );

      if (isPixelSupported(PIXELS.gtm)) {
        pushToDataLayer('remove_from_cart', {
          ecommerce: {
            currency,
            value: totalPrice.toFixed(2),
          },
          items: [
            {
              variant: lineItem?.node?.merchandise,
              product: lineItem?.node?.merchandise?.product,
              quantity: removedLineQuantity,
            },
          ],
        });
      }

      setLineItemRecency(
        lineItemRecencyRef.current.filter(id => id !== lineItemId)
      );
      updateCart(newCartRemoveLine.cartLinesRemove.cart);

      setCartBusy(false);
    } catch (error) {
      console.error('removeLineItem error:', error);
      setCartBusy(false);
    }
  };

  const hideToastNotification = () => {
    setShowToast(false);
    setToastProduct(null);
  };

  const writePepperjamData = () => {
    if (readBrowserStorage('pjnClickData')) {
      const pepperjamData = readBrowserStorage('pjnClickData');

      writeBrowserStorage('pjn-click', pepperjamData, getCookieDomain());
    }
  };

  // Need to keep this method for backwards compatibility on ifb site
  const formatPrice = (price: number, fractionDigits?: number) =>
    currency === 'CAD'
      ? formatCadPrice(price, fractionDigits)
      : localizePrice(price, fractionDigits);

  useEffect(() => {
    (async () => {
      try {
        await retry(
          async () => {
            const initCart = await initializeCart(
              shopifyClient,
              shopifySettingsForCurrency
            );

            // Restore most-recently-added order from the previous session so a
            // page refresh keeps the cart sorted until the next add.
            try {
              const storedRecency = globalThis?.sessionStorage?.getItem(
                LINE_ITEM_RECENCY_KEY
              );
              const parsed = storedRecency ? JSON.parse(storedRecency) : [];
              lineItemRecencyRef.current = Array.isArray(parsed) ? parsed : [];
            } catch {
              lineItemRecencyRef.current = [];
            }

            if (!initCart) updateCart(defaultState.cart);
            updateCart(initCart);
            writePepperjamData();
          },
          {
            maxTimeout: 1000,
            retries: 3,
          }
        );
      } catch (error) {
        console.error('Cart could not be created.', error);
        setCartError(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, shopifyClient, shopifySettingsForCurrency]);

  useEffect(() => {
    cartRef.current = cart;
  });

  useEffect(() => {
    if (!cart.id) return;
    if (!isPixelSupported(PIXELS.rakuten)) return;

    const currentAttributeValue = (key: string) =>
      cart.attributes?.find(a => a.key === key)?.value ?? '';

    const tryWriteRakutenAttribution = ({
      consentKnown,
    }: {
      consentKnown: boolean;
    }) => {
      const attribution = getRakutenAttribution();

      if (!attribution) {
        // Only strip if consent state is definitive. Before OptanonWrapper has
        // run, null attribution means "not yet initialized" not "denied" — so
        // stripping here would race with the write from the consent_ready handler.
        if (!consentKnown) return;
        if (!currentAttributeValue('attributionId')) return; // already stripped
        updateCartAttributes([
          { key: 'attributionId', value: '' },
          { key: 'attributionSource', value: '' },
          { key: 'attributionClickDate', value: '' },
        ]);
        return;
      }

      if (
        currentAttributeValue('attributionId') === attribution.attributionId &&
        currentAttributeValue('attributionSource') ===
          attribution.attributionSource &&
        currentAttributeValue('attributionClickDate') ===
          attribution.attributionClickDate
      ) {
        return; // already up to date
      }

      updateCartAttributes([
        { key: 'attributionId', value: attribution.attributionId },
        { key: 'attributionSource', value: attribution.attributionSource },
        {
          key: 'attributionClickDate',
          value: attribution.attributionClickDate,
        },
      ]);
    };

    // Re-run on every cart mutation (add/remove/qty change), not just once per
    // cart, so a click that ages past the 30-day window mid-session gets
    // re-evaluated and stripped instead of surviving on the cart indefinitely.
    // consentKnown is read directly from cookie presence rather than a
    // one-shot event flag, since consent_ready may fire before this effect's
    // listener is attached — an event-only flag can get permanently stuck at
    // "unresolved" for the rest of the session.
    tryWriteRakutenAttribution({ consentKnown: isConsentResolved() });

    // On every OptanonWrapper call (accept, reject, or preferences update),
    // consent state is definitive — write or strip accordingly.
    const onConsentReady = () =>
      tryWriteRakutenAttribution({ consentKnown: true });
    globalThis.addEventListener('consent_ready', onConsentReady);
    return () =>
      globalThis.removeEventListener('consent_ready', onConsentReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.id, cart.totalQuantity]);

  const value = useMemo(
    () => ({
      cart,
      cartActive,
      cartBusy,
      cartError,
      cartTriggerRef,
      deleteLineItem,
      addToCart,
      emptyCart,
      updateLineItems,
      incrementLineItem,
      decrementLineItem,
      setCartActive: setCartActiveHandler,
      showToast,
      hideToast: hideToastNotification,
      hideToastNotification,
      toastProduct,
      toastAddOns,
      updateCartAttributes,
      shopifyClient,
      formatPrice,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cart, cartActive, cartBusy, cartError, showToast, shopifyClient]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export { CartContext, CartProvider };
